/**
 * VAST Tracker for handling ad event tracking.
 *
 * Fires impression, quartile, interaction, and error pixels per the IAB spec.
 * Manages macro replacement in tracking URLs and deduplicates fired events.
 */

import type { ParsedAd, TrackingEventType, MacroData } from './types'

export class VASTTracker {
  private ad: ParsedAd
  private firedUrls = new Set<string>()

  // Playhead state for macro replacement
  private contentTime = 0
  private adTime = 0
  private playerWidth: number
  private playerHeight: number

  // Quartile deduplication
  private firedStart = false
  private firedFirstQuartile = false
  private firedMidpoint = false
  private firedThirdQuartile = false
  private firedComplete = false
  private firedImpression = false

  constructor(ad: ParsedAd, playerWidth = 0, playerHeight = 0) {
    this.ad = ad
    this.playerWidth = playerWidth
    this.playerHeight = playerHeight
  }

  /**
   * Update player dimensions (e.g. on resize or fullscreen)
   */
  setPlayerSize(width: number, height: number): void {
    this.playerWidth = width
    this.playerHeight = height
  }

  /**
   * Update playhead positions for macro replacement in subsequent tracking calls.
   */
  updatePlayhead(contentTime: number, adTime: number): void {
    this.contentTime = contentTime
    this.adTime = adTime
  }

  /**
   * Check current ad progress and fire quartile events as thresholds are crossed.
   * Should be called on every `timeupdate` from the ad video element.
   */
  checkQuartiles(currentTime: number, duration: number): void {
    if (duration <= 0) return

    const percent = currentTime / duration

    if (!this.firedStart && currentTime > 0) {
      this.firedStart = true
      this.trackEvent('start')
      this.trackEvent('creativeView')
    }

    if (!this.firedFirstQuartile && percent >= 0.25) {
      this.firedFirstQuartile = true
      this.trackEvent('firstQuartile')
    }

    if (!this.firedMidpoint && percent >= 0.5) {
      this.firedMidpoint = true
      this.trackEvent('midpoint')
    }

    if (!this.firedThirdQuartile && percent >= 0.75) {
      this.firedThirdQuartile = true
      this.trackEvent('thirdQuartile')
    }
  }

  /**
   * Fire the complete event. Called when the ad video ends naturally.
   */
  trackComplete(): void {
    if (this.firedComplete) return
    this.firedComplete = true
    this.trackEvent('complete')
  }

  /**
   * Track impression event (fired once when the ad starts playing).
   */
  async trackImpression(): Promise<void> {
    if (this.firedImpression) return
    this.firedImpression = true
    await this.fireTrackingUrls(this.ad.impressionUrls)
  }

  /**
   * Track a specific event type.
   */
  async trackEvent(eventType: TrackingEventType): Promise<void> {
    const event = this.ad.trackingEvents.find(e => e.event === eventType)
    if (event) {
      await this.fireTrackingUrls(event.urls)
    }
  }

  /**
   * Track error with IAB error code.
   */
  async trackError(errorCode: number): Promise<void> {
    const errorUrls = this.ad.errorUrls.map(url =>
      this.replaceMacros(url, { ERRORCODE: errorCode.toString() })
    )
    await this.fireTrackingUrls(errorUrls)
  }

  /**
   * Track click event.
   */
  async trackClick(): Promise<void> {
    await this.fireTrackingUrls(this.ad.clickTracking)
  }

  // Convenience methods for interaction events
  async trackPause(): Promise<void> { await this.trackEvent('pause') }
  async trackResume(): Promise<void> { await this.trackEvent('resume') }
  async trackMute(): Promise<void> { await this.trackEvent('mute') }
  async trackUnmute(): Promise<void> { await this.trackEvent('unmute') }
  async trackSkip(): Promise<void> { await this.trackEvent('skip') }
  async trackFullscreen(): Promise<void> { await this.trackEvent('fullscreen') }
  async trackExitFullscreen(): Promise<void> { await this.trackEvent('exitFullscreen') }

  /**
   * Fire tracking URLs with no-cors mode and keepalive.
   */
  private async fireTrackingUrls(urls: string[]): Promise<void> {
    const promises = urls
      .filter(url => !this.firedUrls.has(url))
      .map(async url => {
        try {
          const finalUrl = this.replaceMacros(url)
          this.firedUrls.add(url)

          await fetch(finalUrl, {
            method: 'GET',
            mode: 'no-cors',
            cache: 'no-cache',
            keepalive: true,
          })
        } catch (error) {
          console.debug('Tracking pixel failed:', url, error)
        }
      })

    await Promise.allSettled(promises)
  }

  /**
   * Replace VAST macros in a tracking URL.
   */
  private replaceMacros(url: string, additionalMacros: Partial<MacroData> = {}): string {
    const macros: MacroData = {
      CACHEBUSTER: Math.round(Math.random() * 1.0e10).toString(),
      TIMESTAMP: new Date().toISOString(),
      ASSETURI: encodeURIComponent(window.location.href),
      CONTENTPLAYHEAD: this.formatTime(this.contentTime),
      MEDIAPLAYHEAD: this.formatTime(this.adTime),
      PLAYERSIZE: this.playerWidth && this.playerHeight
        ? `${this.playerWidth}x${this.playerHeight}`
        : `${window.innerWidth}x${window.innerHeight}`,
      PLAYERSTATE: 'normal',
      INVENTORYSTATE: 'normal',
      CLICKPOS: '0,0',
      ...additionalMacros,
    }

    let finalUrl = url

    // Replace standard macros in both [MACRO] and %%MACRO%% formats
    Object.entries(macros).forEach(([macro, value]) => {
      finalUrl = finalUrl
        .replace(new RegExp(`\\[${macro}\\]`, 'g'), value)
        .replace(new RegExp(`%%${macro}%%`, 'g'), value)
    })

    // Replace aliases
    finalUrl = finalUrl
      .replace(/\[RANDOM\]/g, macros.CACHEBUSTER)
      .replace(/%%RANDOM%%/g, macros.CACHEBUSTER)
      .replace(/\[UNIX_TIMESTAMP\]/g, Date.now().toString())
      .replace(/%%UNIX_TIMESTAMP%%/g, Date.now().toString())

    return finalUrl
  }

  /**
   * Format seconds to HH:MM:SS.mmm for VAST playhead macros.
   */
  private formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    const pad = (n: number, digits = 2): string =>
      n.toString().padStart(digits, '0')

    const secsStr = secs.toFixed(3).padStart(6, '0')
    return `${pad(hours)}:${pad(minutes)}:${secsStr}`
  }
}
